import React, { useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import { format, addDays, startOfWeek, endOfWeek, isSameDay, isWithinInterval } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

// Registrar localização pt-BR
registerLocale('pt-BR', ptBR);

/**
 * Componente de calendário para seleção de data
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Date} props.selectedDate - Data selecionada
 * @param {Function} props.onChange - Função chamada ao mudar a data
 * @param {Array} props.disabledDates - Datas desabilitadas
 * @param {string} props.primaryColor - Cor primária para destaque
 */
const Calendar = ({
  selectedDate,
  onChange,
  disabledDates = [],
  minDate = new Date(),
  primaryColor = '#4F46E5'
}) => {
  const [showFullCalendar, setShowFullCalendar] = useState(false);
  
  // Função para verificar se uma data deve ser desabilitada
  const isDateDisabled = (date) => {
    // Verificar se é domingo (0) e está na lista de dias desabilitados
    if (date.getDay() === 0 && disabledDates.includes('sunday')) {
      return true;
    }
    
    // Verificar se a data está na lista de datas específicas desabilitadas
    return disabledDates.some(disabledDate => {
      if (disabledDate instanceof Date) {
        return (
          date.getDate() === disabledDate.getDate() &&
          date.getMonth() === disabledDate.getMonth() &&
          date.getFullYear() === disabledDate.getFullYear()
        );
      }
      return false;
    });
  };
  
  // Estilo para as datas selecionadas
  const customDayStyle = (date, selectedDate) => {
    const isSelected = 
      selectedDate && 
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear();
    
    if (isSelected) {
      return {
        backgroundColor: primaryColor,
        color: 'white',
        borderRadius: '50%'
      };
    }
    return {};
  };

  // Gerar dias para visualização horizontal (duas semanas)
  const generateTwoWeeksDays = () => {
    const days = [];
    const startDate = new Date();
    
    // Gerar 14 dias a partir de hoje
    for (let i = 0; i < 14; i++) {
      const currentDate = addDays(startDate, i);
      
      // Pular domingos se estiverem desabilitados
      if (currentDate.getDay() === 0 && disabledDates.includes('sunday')) {
        continue;
      }
      
      // Pular datas desabilitadas específicas
      if (isDateDisabled(currentDate)) {
        continue;
      }
      
      days.push(currentDate);
    }
    
    return days;
  };
  
  // Dias para visualização horizontal
  const twoWeeksDays = generateTwoWeeksDays();
  
  // Alternar entre visualização horizontal e calendário completo
  const toggleCalendarView = () => {
    setShowFullCalendar(!showFullCalendar);
  };
  
  return (
    <div className="calendar-container">
      {/* <h3 className="text-lg font-semibold mb-4">Selecione a data e horário</h3> */}
      
      {!showFullCalendar ? (
        // Visualização horizontal de duas semanas
        <div className="mb-4">
          <div className="overflow-x-auto pb-2">
            <div className="flex space-x-2">
              {twoWeeksDays.map((date, index) => (
                <div
                  key={index}
                  className={`flex flex-col items-center p-2 cursor-pointer border rounded-lg ${
                    selectedDate && isSameDay(date, selectedDate)
                      ? 'text-white'
                      : 'hover:bg-gray-100'
                  }`}
                  style={
                    selectedDate && isSameDay(date, selectedDate)
                      ? { backgroundColor: primaryColor, color: 'white' }
                      : {}
                  }
                  onClick={() => onChange(date)}
                >
                  <span className="text-xs font-medium">
                    {format(date, 'EEE', { locale: ptBR }).toUpperCase()}
                  </span>
                  <span className="text-xl font-bold">{format(date, 'd')}</span>
                  <span className="text-xs">{format(date, 'MMM', { locale: ptBR })}</span>
                </div>
              ))}
            </div>
          </div>
          
          <button
            type="button"
            className="mt-2 text-sm text-gray-600 hover:text-gray-900 flex items-center"
            onClick={toggleCalendarView}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Ver calendário completo
          </button>
        </div>
      ) : (
        // Calendário completo
        <div className="mb-4">
          <ReactDatePicker
            selected={selectedDate}
            onChange={(date) => {
              onChange(date);
              setShowFullCalendar(false);
            }}
            inline
            locale="pt-BR"
            minDate={minDate}
            filterDate={date => !isDateDisabled(date)}
            monthsShown={1}
            showDisabledMonthNavigation
            dayClassName={date => {
              return selectedDate && 
                date.getDate() === selectedDate.getDate() &&
                date.getMonth() === selectedDate.getMonth() &&
                date.getFullYear() === selectedDate.getFullYear()
                ? "react-datepicker__day--selected"
                : "";
            }}
            renderDayContents={(day, date) => {
              return (
                <div
                  style={customDayStyle(date, selectedDate)}
                  className="w-full h-full flex items-center justify-center"
                >
                  {day}
                </div>
              );
            }}
          />
          
          <button
            type="button"
            className="mt-2 text-sm text-gray-600 hover:text-gray-900 flex items-center"
            onClick={toggleCalendarView}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Fechar calendário
          </button>
        </div>
      )}
    </div>
  );
};

export default Calendar; 